import React, { ChangeEvent, useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { NavLink } from "react-router-dom";
import { fetchContracts, resetContractsState, DocumentDetails } from "../../store/document/contractsSlice";
import { fetchStatements, resetStatementsState } from "../../store/document/statementsSlice";
import { AppDispatch, RootState } from "../../store/indexRedux";
import { useTranslation } from "react-i18next";
import MultiSelectDropdown from "../ui/MultiSelectDropdown";
import SearchInput from "../ui/SearchInput";
import Spinner from "../ui/Spinner";

const TYPE_BADGE: Record<string, { bg: string; text: string }> = {
  "Completed Agreement":        { bg: "bg-primary-50",  text: "text-primary-700" },
  "Signed Mandate":             { bg: "bg-primary-100", text: "text-primary-800" },
  "Switch Confirmation":        { bg: "bg-accent-50",   text: "text-accent-700"  },
  "EOC Confirmation":           { bg: "bg-green-50",    text: "text-green-700"   },
  "Statement":                  { bg: "bg-gray-100",    text: "text-gray-600"    },
  "General Terms and Conditions": { bg: "bg-green-50",  text: "text-green-600"   },
};

const DocumentsTable: React.FC = () => {
  const { t } = useTranslation("DocumentsTable");
  const dispatch = useDispatch<AppDispatch>();

  const { contracts, loading: loadingContracts } = useSelector((state: RootState) => state.contracts);
  const { statements, loading: loadingStatements } = useSelector((state: RootState) => state.statements);
  const { selectedUserCompany } = useSelector((state: RootState) => state.selectedCompany);

  const [sortOrder, setSortOrder] = useState("desc");
  const [showStatements, setShowStatements] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [displayedDocuments, setDisplayedDocuments] = useState<DocumentDetails[]>([]);

  const isLoading = useMemo(
    () => loadingContracts || loadingStatements,
    [loadingContracts, loadingStatements]
  );

  const completedAgreements = useMemo(
    () => contracts.filter((doc) => doc.docType === "Completed Agreement"),
    [contracts]
  );

  const shouldShowTermsAndConditions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const cutoffDate = new Date(currentYear, 9, 1);
    return completedAgreements.some((doc) => new Date(doc.date) < cutoffDate);
  }, [completedAgreements]);

  const combinedDocuments: DocumentDetails[] = useMemo(() => {
    if (showStatements) return statements;

    const docs = [...contracts];
    if (shouldShowTermsAndConditions && completedAgreements.length > 0) {
      docs.push({
        documentCode: completedAgreements[0].documentCode,
        date: "2025-09-26",
        documentName: "Wijziging contractformule",
        ean: "",
        fileName: "newtermsandconditions.pdf",
        docType: "General Terms and Conditions",
      });
    }
    return docs;
  }, [contracts, statements, showStatements, shouldShowTermsAndConditions, completedAgreements]);

  const fetchDocuments = useCallback(() => {
    if (!selectedUserCompany?.companyNumber) return;
    dispatch(resetContractsState());
    dispatch(resetStatementsState());
    if (showStatements) {
      dispatch(fetchStatements(selectedUserCompany.companyNumber));
    } else {
      dispatch(fetchContracts(selectedUserCompany.companyNumber));
    }
  }, [dispatch, selectedUserCompany?.companyNumber, showStatements]);

  const filterDocuments = useCallback(() => {
    const q = searchValue.toLowerCase();
    const filtered = combinedDocuments.filter((doc) => {
      const matchSearch =
        doc.documentCode.toLowerCase().includes(q) ||
        doc.documentName.toLowerCase().includes(q) ||
        (doc.ean ?? "").toLowerCase().includes(q) ||
        doc.date.includes(q);
      const matchType = selectedTypes.length === 0 || selectedTypes.includes(doc.docType);
      return matchSearch && matchType;
    });

    filtered.sort((a, b) => {
      const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
      return sortOrder === "asc" ? diff : -diff;
    });

    setDisplayedDocuments(filtered);
  }, [combinedDocuments, searchValue, selectedTypes, sortOrder]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments, selectedUserCompany?.companyNumber]);
  useEffect(() => { filterDocuments(); }, [filterDocuments, selectedUserCompany?.companyNumber]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value);

  const toggleDocumentsType = (type: "agreements" | "statements") => {
    setShowStatements(type === "statements");
    setSelectedTypes([]);
  };

  const documentTypes = showStatements
    ? [{ value: "Statement", label: t("statement") }]
    : [
        { value: "Completed Agreement",          label: t("completedAgreement") },
        { value: "EOC Confirmation",             label: t("eocConfirmation") },
        { value: "Signed Mandate",               label: t("signedMandate") },
        { value: "Switch Confirmation",          label: t("switchConfirmation") },
        { value: "General Terms and Conditions", label: t("termsAndConditions") || "Terms and Conditions" },
      ];

  const docRoute = (doc: DocumentDetails) => {
    if (doc.docType === "Statement")                  return `/statements/${doc.fileName}`;
    if (doc.docType === "Switch Confirmation")        return `/switch/${doc.fileName}`;
    if (doc.docType === "EOC Confirmation")           return `/eoc/${doc.fileName}`;
    if (doc.docType === "Signed Mandate")             return `/mandates/${doc.fileName}`;
    if (doc.docType === "General Terms and Conditions") return `/terms/${doc.fileName}`;
    return `/documents/${doc.fileName}`;
  };

  return (
    <div className="md:mx-16 pb-[80px]">
      <div className="mt-20 mb-6">
        <h1 className="text-primary-700 dark:text-white text-2xl font-semibold tracking-tight">
          {t("documentTitle")}
        </h1>
      </div>

      {/* Card */}
      <div className="rounded-xl border border-gray-200 dark:border-primary-800 bg-white dark:bg-primary-900 overflow-hidden shadow-sm">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-primary-800">
          {/* Toggle */}
          <div className="flex rounded-lg border border-gray-200 dark:border-primary-700 overflow-hidden shrink-0">
            <button
              onClick={() => toggleDocumentsType("agreements")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                !showStatements
                  ? "bg-primary-700 text-white"
                  : "bg-white dark:bg-primary-900 text-gray-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-primary-800"
              }`}
            >
              {t("contracts")}
            </button>
            <button
              onClick={() => toggleDocumentsType("statements")}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-200 dark:border-primary-700 transition-colors ${
                showStatements
                  ? "bg-primary-700 text-white"
                  : "bg-white dark:bg-primary-900 text-gray-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-primary-800"
              }`}
            >
              {t("statements")}
            </button>
          </div>

          {/* Search */}
          <SearchInput
            inputClassName="h-9 text-sm"
            onChange={handleSearchChange}
            placeholder={t("searchDocumentsPlaceholder")}
          />

          {/* Filter */}
          {!showStatements && (
            <MultiSelectDropdown
              options={documentTypes}
              selectedValues={selectedTypes}
              onChange={setSelectedTypes}
              placeholder={t("selectDocumentType")}
              styles="h-9"
            />
          )}
        </div>

        {/* Scrollable table area */}
        <div className="overflow-x-auto" style={{ maxHeight: "65vh", overflowY: "scroll" }}>
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th
                  onClick={() => setSortOrder((p) => (p === "asc" ? "desc" : "asc"))}
                  className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-b border-gray-200 dark:border-primary-800 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-primary-400 cursor-pointer select-none whitespace-nowrap w-[120px]"
                >
                  {t("tableHeaders.date")} {sortOrder === "asc" ? "↑" : "↓"}
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-b border-gray-200 dark:border-primary-800 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-primary-400">
                  {t("tableHeaders.documentName")}
                </th>
                {!showStatements && (
                  <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-b border-gray-200 dark:border-primary-800 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-primary-400 whitespace-nowrap w-[200px]">
                    {t("tableHeaders.ean")}
                  </th>
                )}
                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-b border-gray-200 dark:border-primary-800 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-primary-400 w-[240px]">
                  {t("tableHeaders.type")}
                </th>
                <th className="sticky top-0 z-10 bg-gray-50 dark:bg-primary-950 border-b border-gray-200 dark:border-primary-800 px-4 py-2.5 w-[100px]" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={showStatements ? 4 : 5} className="py-12 text-center">
                    <Spinner />
                  </td>
                </tr>
              ) : displayedDocuments.length === 0 ? (
                <tr>
                  <td
                    colSpan={showStatements ? 4 : 5}
                    className="py-12 text-center text-sm text-gray-400 dark:text-primary-500"
                  >
                    {t("noResultsFound")}
                  </td>
                </tr>
              ) : (
                displayedDocuments.map((doc, i) => {
                  const isCryptoSigned = doc.fileName.includes("signedencrypted");
                  const isSigned = !isCryptoSigned && doc.fileName.includes("-signed.pdf");
                  const badge = TYPE_BADGE[doc.docType] ?? { bg: "bg-gray-100", text: "text-gray-600" };
                  return (
                    <tr
                      key={i}
                      className="group hover:bg-gray-50 dark:hover:bg-primary-950/40 transition-colors"
                    >
                      {/* Date */}
                      <td className="px-4 py-3.5 border-t border-gray-100 dark:border-primary-800 text-gray-700 dark:text-primary-200 whitespace-nowrap align-top">
                        {doc.date}
                      </td>

                      {/* Document name */}
                      <td className="px-4 py-3.5 border-t border-gray-100 dark:border-primary-800 text-gray-800 dark:text-primary-100 align-top">
                        {doc.documentName}
                      </td>

                      {/* EAN */}
                      {!showStatements && (
                        <td className="px-4 py-3.5 border-t border-gray-100 dark:border-primary-800 text-gray-500 dark:text-primary-400 text-xs font-mono align-top whitespace-nowrap">
                          {doc.ean || "–"}
                        </td>
                      )}

                      {/* Type badge */}
                      <td className="px-4 py-3.5 border-t border-gray-100 dark:border-primary-800 align-top">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}
                          >
                            {doc.docType}
                          </span>
                          {isSigned && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-300">
                              Signed
                            </span>
                          )}
                          {isCryptoSigned && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3A5.25 5.25 0 0012 1.5zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clipRule="evenodd" />
                              </svg>
                              Digitally Signed
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Action */}
                      <td className="px-4 py-3.5 border-t border-gray-100 dark:border-primary-800 text-right align-top">
                        <NavLink
                          to={docRoute(doc)}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200 hover:underline underline-offset-2"
                        >
                          {t("download")}
                        </NavLink>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DocumentsTable;
